import { useState } from "react";
import { motion } from "framer-motion";
import {
  Calendar,
  Clock,
  Video,
  MapPin,
  Sparkles,
  CheckCircle2,
  Circle,
  Users,
  FileText,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/shared/PageHeader";
import StatCard from "@/components/shared/StatCard";
import SectionHeader from "@/components/shared/SectionHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MEETINGS, MEETING_TASKS } from "@/data/mockData";

export default function MeetingIntel() {
  const [selected, setSelected] = useState(MEETINGS[0]);
  const [tasks, setTasks] = useState(MEETING_TASKS);

  const toggle = (id) =>
    setTasks((ts) => ts.map((t) => (t.id === id ? { ...t, done: !t.done } : t)));

  return (
    <div data-testid="meetings-page">
      <PageHeader
        eyebrow="Module 08 · Meeting Booking Intelligence"
        title="Every meeting, briefed by AI."
        description="From Calendly to your website widget — meetings auto-create CRM opportunities with pre-call research, agendas and post-call follow-ups."
        actions={
          <Button data-testid="meetings-connect-cal" className="rounded-full bg-indigo-500 hover:bg-indigo-400">
            <Calendar className="h-3.5 w-3.5 mr-1.5" /> Connect calendar
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <StatCard testId="meet-stat-today"     label="Meetings Today" value="2"  delta="+1"    accent="indigo" icon={Calendar} />
        <StatCard testId="meet-stat-upcoming"  label="Upcoming · 7d"  value="14" delta="+3"    accent="cyan" />
        <StatCard testId="meet-stat-noshow"    label="No-shows"       value="1"  delta="0"     accent="pink" />
        <StatCard testId="meet-stat-converted" label="Meetings Won"   value="9"  delta="+2"    accent="emerald" />
        <StatCard testId="meet-stat-conv"      label="Proposal → Close" value="41" suffix="%" delta="+4pt" accent="amber" />
        <StatCard testId="meet-stat-forecast"  label="Forecast"       value="612k" prefix="$" delta="+22%" accent="indigo" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Timeline */}
        <div className="lg:col-span-2">
          <SectionHeader kicker="Chronological" title="Upcoming meetings" />
          <div className="space-y-2" data-testid="meetings-list">
            {MEETINGS.map((m, i) => (
              <motion.button
                key={m.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                onClick={() => setSelected(m)}
                data-testid={`meeting-${m.id}`}
                className={`glass-card w-full text-left p-4 hover:translate-y-[-2px] transition-transform ${
                  selected?.id === m.id ? "!border-indigo-400/40" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-1.5 text-cyan-300">
                    <Clock className="h-3 w-3" />
                    <span className="font-mono text-[11px]">{m.when}</span>
                    <span className="text-white/30">·</span>
                    <span className="font-mono text-[11px] text-white/60">{m.duration}</span>
                  </div>
                  <Badge className={`rounded-full font-mono text-[10px] uppercase tracking-wider hover:bg-transparent border ${
                    m.status === "Confirmed"
                      ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/25"
                      : "bg-amber-500/10 text-amber-300 border-amber-500/25"
                  }`}>
                    {m.status === "Confirmed" && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 mr-1 shadow-[0_0_4px_#10b981]" />}
                    {m.status}
                  </Badge>
                </div>
                <div className="text-[14px] text-white">{m.prospect}</div>
                <div className="text-[12px] text-white/50">{m.role} · {m.company}</div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-[11.5px] text-white/50">
                    {m.channel.startsWith("In-person") ? <MapPin className="h-3 w-3" /> : <Video className="h-3 w-3" />}
                    <span>{m.channel}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-white/40">Q</span>
                    <span className="font-mono text-[13px]">{m.quality}</span>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-3">
          {selected && (
            <motion.div
              key={selected.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6"
              data-testid="meeting-detail"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-2 text-cyan-300">
                    <Clock className="h-3.5 w-3.5" />
                    <span className="font-mono text-[12px]">{selected.when}</span>
                    <span className="text-white/30">·</span>
                    <span className="font-mono text-[12px] text-white/60">{selected.duration}</span>
                    <span className="text-white/30">·</span>
                    <span className="font-mono text-[12px] text-white/60">{selected.channel}</span>
                  </div>
                  <div className="font-display text-2xl text-white leading-tight">
                    {selected.prospect}
                  </div>
                  <div className="text-[13px] text-white/60 mt-0.5">
                    {selected.role} · {selected.company}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-2xl">{selected.quality}</div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-white/40">quality</div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <MiniBox label="Source"   value={selected.source} />
                <MiniBox label="History"  value={`${selected.history} interactions`} />
              </div>

              <div className="mt-5 rounded-xl border border-indigo-500/20 bg-indigo-500/[0.06] p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-3.5 w-3.5 text-indigo-300" />
                  <span className="font-mono text-[10px] uppercase tracking-wider text-indigo-300">
                    AI pre-call brief
                  </span>
                </div>
                <p className="text-[13.5px] text-white/85 leading-relaxed">{selected.prep}</p>
              </div>

              <div className="mt-5">
                <div className="font-mono text-[10px] uppercase tracking-wider text-white/50 mb-2">
                  Meeting agenda
                </div>
                <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.05]">
                  {selected.agenda.map((line, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-2.5 text-[13.5px] text-white/80">
                      <span className="font-mono text-[11px] text-white/40 w-6">{String(i + 1).padStart(2, "0")}</span>
                      <span>{line}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <Button
                  data-testid="meeting-join"
                  className="rounded-full bg-indigo-500 hover:bg-indigo-400"
                  onClick={() => toast.success(`Joining ${selected.prospect} · ${selected.company}`)}
                >
                  <Video className="h-3.5 w-3.5 mr-1.5" /> Join meeting
                </Button>
                <Button variant="ghost" className="rounded-full text-white/70 border border-white/[0.06]">
                  <FileText className="h-3.5 w-3.5 mr-1.5" /> Attach proposal
                </Button>
                <Button variant="ghost" className="rounded-full text-white/70 border border-white/[0.06]">
                  <ChevronRight className="h-3.5 w-3.5 mr-1.5" /> Open in CRM
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Follow-ups */}
      <SectionHeader className="mt-10" kicker="Auto-generated after each meeting" title="Follow-up tasks" />
      <div className="glass-card p-2 divide-y divide-white/[0.05]" data-testid="meetings-tasks">
        {tasks.map((t) => (
          <button
            key={t.id}
            onClick={() => toggle(t.id)}
            data-testid={`meetings-task-${t.id}`}
            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.02] transition-colors"
          >
            {t.done
              ? <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              : <Circle className="h-4 w-4 text-white/30" />
            }
            <span className={`flex-1 text-[13.5px] ${t.done ? "line-through text-white/40" : "text-white/85"}`}>
              {t.label}
            </span>
            <span className="font-mono text-[11px] text-white/40">{t.due}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function MiniBox({ label, value }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
      <div className="font-mono text-[10px] uppercase tracking-wider text-white/50">{label}</div>
      <div className="mt-0.5 text-[13px] text-white">{value}</div>
    </div>
  );
}
